import { Injectable } from '@angular/core';
import { DatexPipe } from '../pipes/date-format-pipe/datex';
import { BaseDataService } from './base.data.service';
import { HttpClient } from '@angular/common/http';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import { TravelSchedulerDTO } from '../interfaces/travel-scheduler/travel-scheduler-task';
import {
  TimeWindowResponse,
  TimeWindowResponseDto,
  TimeSlot,
} from '../interfaces/travel-scheduler/time-window-response';
import { UpdateSupplierScheduleRequest } from '../interfaces/travel-scheduler/update-supplier-schedule-request';

@Injectable({
  providedIn: 'root',
})
export class TravelSchedulerService extends BaseDataService {
  public url = '/api/travel-scheduler';

  constructor(
    public http: HttpClient,
    handlersFactory: HttpResponsesHandlersFactory,
    private dateXPipe: DatexPipe,
  ) {
    super(http, handlersFactory);
  }

  public async updateSupplierSchedule(request: UpdateSupplierScheduleRequest) {
    await this.http.put(`${this.url}/supplier-schedule`, request).toPromise();
  }

  /**
   * @param supplierId The supplier the schedule is being set for. Only needed when the job has not been
   * allocated to a supplier yet, such as while a job is being deployed.
   */
  public async get(jobId: number, supplierId?: number): Promise<TravelSchedulerDTO> {
    let uri = `${this.url}?jobId=${jobId}`;
    if (supplierId != null) {
      uri += `&supplierId=${supplierId}`;
    }

    const res = await this.http.get<TravelSchedulerDTO>(uri).toPromise();
    if (res && res.latestHandBackDateTime) {
      res.latestHandBackDateTime = new Date(res.latestHandBackDateTime);
    }
    if (res && res.tasks) {
      res.tasks = res.tasks.map((task) => ({
        ...task,
        supplierPickupWindow: task.supplierPickupWindow
          ? new Date(task.supplierPickupWindow)
          : null,
        supplierDropoffWindow: task.supplierDropoffWindow
          ? new Date(task.supplierDropoffWindow)
          : null,
        customerPickupEarliest: task.customerPickupEarliest
          ? new Date(task.customerPickupEarliest)
          : null,
        customerPickupLatest: task.customerPickupLatest
          ? new Date(task.customerPickupLatest)
          : null,
        customerDropoffEarliest: task.customerDropoffEarliest
          ? new Date(task.customerDropoffEarliest)
          : null,
        customerDropoffLatest: task.customerDropoffLatest
          ? new Date(task.customerDropoffLatest)
          : null,
      }));
    }
    return res;
  }

  public async getAvailablePickupWindows(
    taskId: number,
    previousTaskDropoffWindow: Date,
  ): Promise<TimeWindowResponse> {
    let uri = `${this.url}/available-pickup-windows?TaskId=${taskId}`;
    if (previousTaskDropoffWindow != null) {
      uri += `&PreviousTaskDropoffWindow=${previousTaskDropoffWindow.toISOString()}`;
    }

    const res = await this.http.get<TimeWindowResponseDto>(uri).toPromise();

    return this.MapFromDto(res);
  }

  private MapFromDto(timeWindowResponseDto: TimeWindowResponseDto): TimeWindowResponse {
    let dataStructure: TimeWindowResponse = {
      dateOptions: timeWindowResponseDto.dateOptions.map((d) => ({
        date: new Date(d.date),
        label: d.label,
        subLabel: d.subLabel,
      })),
      timeWindowLookup: timeWindowResponseDto.timeWindowLookup.map((ds) => ({
        date: new Date(ds.date),
        timeSlots: ds.timeSlots.map((ts) => ({
          startTime: new Date(ts.startTime),
          label: ts.label,
          warningMessage: ts.warningMessage,
        })),
      })),
    };

    return dataStructure;
  }

  public async getAvailableDropoffWindows(
    taskId: number,
    selectedPickupWindow: Date,
    canCarryover: boolean,
  ): Promise<TimeWindowResponse> {
    let dateISO = new Date(selectedPickupWindow).toISOString();
    const res = await this.http
      .get<TimeWindowResponseDto>(
        `${this.url}/available-dropoff-windows?TaskId=${taskId}&SelectedPickupWindow=${dateISO}&carryoverSelected=${canCarryover}`,
      )
      .toPromise();

    return this.MapFromDto(res);
  }

  formatScheduleWindowText(date: Date) {
    return this.formatScheduleWindowTextWithTime(date, date);
  }

  formatScheduleWindowTextWithTime(date: Date, time: Date) {
    if (date == null) {
      return '';
    }
    let dateText = this.dateXPipe.transform(date, 'ddd, DD MMM');

    if (time == null) {
      return dateText;
    }

    let scheduleTime = this.dateXPipe.transform(time, 'HH:mm');

    let hourAfter: Date = new Date(time);
    hourAfter.setHours(hourAfter.getHours() + 1);
    let hourAfterText = this.dateXPipe.transform(hourAfter, 'HH:mm');

    let text = dateText + ', ' + scheduleTime + ' - ' + hourAfterText;
    return text;
  }
}
